import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, AlertTriangle, XCircle, Calculator, Search, X, Shield, Save, FileText, Info } from 'lucide-react';
import { PARAMS, FORMAS_PAGAMENTO, MOTIVOS_LIBERACAO } from '../config/parametrosCrediario';
import { ehAvista, markupCategoria, precoAvistaPadrao, precoSugerido as calcPrecoSugerido, avaliarPreco } from '../lib/viabilidade';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtBRL(v) {
  if (!isFinite(v)) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtPct(v, digits = 2) {
  if (!isFinite(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

function normalizar(s) {
  return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

// Quando o produto é buscado no ERP, o custo vem da tabela de preço "Custo Médio".
// Alternativamente, o usuário pode digitar um custo de referência (modo manual).
function escolherTabelaCusto(precos) {
  return (precos || []).find(p => normalizar(p.Nome_Tabela).includes('CUSTO MEDIO')) || null;
}

const RESULTADO_INFO = {
  viavel: { texto: 'Viável', Icon: CheckCircle, badge: 'bg-green-100 text-green-800' },
  gerente: { texto: 'Necessita liberação do(a) gerente', Icon: AlertTriangle, badge: 'bg-red-100 text-red-700' },
  inviavel: { texto: 'Inviável', Icon: XCircle, badge: 'bg-red-600 text-white' },
};

function BadgeResultado({ resultado }) {
  const { texto, Icon, badge } = RESULTADO_INFO[resultado];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${badge}`}>
      <Icon size={12} />{texto}
    </span>
  );
}

// Prazos exibidos na tabela de preço sugerido (além do à vista)
const PRAZOS_TABELA = [1, 3, 6, 10, 12];

// Gravado em custo_tabela_nome quando o custo foi digitado (não veio do ERP)
const CUSTO_DIGITADO = 'Digitado manualmente';

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SimuladorViabilidadePage() {
  const { isGerenteOuAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [carregandoSimulacao, setCarregandoSimulacao] = useState(false);

  // Produto / venda
  const [categoria, setCategoria] = useState('eletro');
  const [custo, setCusto] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('avista');
  const [parcelas, setParcelas] = useState('12');
  const [precoSolicitado, setPrecoSolicitado] = useState('');

  // Justificativa quando o preço exige liberação do gerente ou é inviável
  const [motivoTipo, setMotivoTipo] = useState('');
  const [motivoTexto, setMotivoTexto] = useState('');

  // Origem do custo: produto buscado no ERP ou custo de referência digitado
  const [origemCusto, setOrigemCusto] = useState('erp');
  const [referenciaTexto, setReferenciaTexto] = useState('');

  // Busca de produto no ERP
  const [produtoTermo, setProdutoTermo] = useState('');
  const [produtoResultados, setProdutoResultados] = useState([]);
  const [buscandoProduto, setBuscandoProduto] = useState(false);
  const [erroProduto, setErroProduto] = useState('');
  const [produtoSel, setProdutoSel] = useState(null);
  const [carregandoCusto, setCarregandoCusto] = useState(false);
  const [custoTabelaNome, setCustoTabelaNome] = useState('');

  // Persistência da simulação (autosave + botão Salvar)
  const simulacaoIdRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const [statusSalvamento, setStatusSalvamento] = useState('');

  // Reabrir uma simulação salva (vindo do histórico de consultas) — preenche os
  // campos com os dados gravados e faz o autosave subsequente atualizar o mesmo registro.
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    setCarregandoSimulacao(true);
    apiService.simulacaoViabilidade.buscarPorId(id)
      .then(resp => {
        const d = resp.data?.data;
        if (!d) return;
        simulacaoIdRef.current = d.id;
        setCategoria(d.categoria || 'eletro');
        setCusto(d.custo_produto != null ? String(d.custo_produto) : '');
        setCustoTabelaNome(d.custo_tabela_nome || '');
        if (d.produto_ordem) {
          setOrigemCusto('erp');
          setProdutoSel({ Ordem: d.produto_ordem, Codigo: d.produto_codigo, Nome: d.produto_nome });
        } else if (d.custo_produto != null) {
          setOrigemCusto('manual');
          setReferenciaTexto(d.produto_nome || '');
        }
        // Simulações antigas (antes da forma de pagamento) — assume crediário quando parcelado
        const nSalvo = Number(d.num_parcelas) || 0;
        setFormaPagamento(d.forma_pagamento || (nSalvo > 0 ? 'crediario' : 'avista'));
        setParcelas(nSalvo > 0 ? String(nSalvo) : '12');
        setPrecoSolicitado(d.preco_solicitado != null ? String(d.preco_solicitado) : '');
        setMotivoTipo(d.motivo_tipo || '');
        setMotivoTexto(d.motivo_texto || '');
        setStatusSalvamento('salvo');
      })
      .catch(() => toast.error('Erro ao carregar a simulação salva.'))
      .finally(() => setCarregandoSimulacao(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const custoNum = parseFloat(custo) || 0;
  const avista = formaPagamento === 'avista';
  const nDigitado = parseInt(parcelas, 10) || 0;
  const n = avista ? 0 : nDigitado;
  const markup = markupCategoria(categoria);
  const precoAvista = custoNum > 0 ? precoAvistaPadrao(custoNum, categoria) : 0;
  const precoSolicitadoNum = parseFloat(precoSolicitado) || 0;
  const forma = FORMAS_PAGAMENTO[formaPagamento];

  // ── Busca de produto no ERP ──
  async function buscarProdutosSimulador(e) {
    e?.preventDefault();
    if (!produtoTermo.trim()) return;
    setBuscandoProduto(true);
    setErroProduto('');
    setProdutoResultados([]);
    try {
      const resp = await apiService.produtos.buscar(produtoTermo);
      const lista = resp.data?.data ?? [];
      setProdutoResultados(lista);
      if (lista.length === 0) setErroProduto('Nenhum produto encontrado.');
    } catch {
      setErroProduto('Erro ao buscar produtos.');
    } finally {
      setBuscandoProduto(false);
    }
  }

  async function selecionarProduto(p) {
    setProdutoSel(p);
    setProdutoResultados([]);
    setProdutoTermo('');
    setErroProduto('');
    setCarregandoCusto(true);
    setCustoTabelaNome('');
    try {
      const resp = await apiService.produtos.buscarPrecos(p.Ordem, { incluirCusto: true, todasTabelas: true });
      const precos = resp.data?.data ?? [];
      const entrada = escolherTabelaCusto(precos);
      if (entrada) {
        setCusto(String(entrada.Preco));
        setCustoTabelaNome(entrada.Nome_Tabela);
        setPrecoSolicitado(''); // novo produto — o preço solicitado anterior não vale mais
      } else {
        setCusto('');
        setErroProduto('Produto sem tabela de Custo Médio cadastrada no ERP — não é possível calcular a viabilidade.');
      }
    } catch {
      setErroProduto('Erro ao buscar preço de custo do produto.');
    } finally {
      setCarregandoCusto(false);
    }
  }

  function limparProduto() {
    setProdutoSel(null);
    setCustoTabelaNome('');
    setCusto('');
    setPrecoSolicitado('');
  }

  // É um ou outro: trocar a origem do custo descarta o que foi preenchido no outro modo.
  function trocarOrigemCusto(origem) {
    if (origem === origemCusto) return;
    limparProduto();
    setOrigemCusto(origem);
    setReferenciaTexto('');
    setProdutoTermo('');
    setProdutoResultados([]);
    setErroProduto('');
  }

  // Sem custo não há dado para calcular. No modo ERP, o custo só vale com produto
  // selecionado (e seu custo médio carregado); no manual, basta o custo digitado.
  const manual = origemCusto === 'manual';
  const temDados = manual ? custoNum > 0 : (!!produtoSel && custoNum > 0);
  const condicaoOk = avista || (nDigitado >= 1 && nDigitado <= PARAMS.parcelasMax);

  // ── Avaliação do preço solicitado ──
  const avaliacao = temDados && condicaoOk
    ? avaliarPreco({ custo: custoNum, categoria, forma: formaPagamento, n, precoSolicitado: precoSolicitadoNum })
    : null;
  const resultado = avaliacao?.resultado ?? null;
  const exigeMotivo = resultado === 'gerente' || resultado === 'inviavel';
  const motivoOk = !exigeMotivo || motivoTipo === 'avaria_estoque_antigo'
    || (motivoTipo === 'outros' && motivoTexto.trim() !== '');

  // ── Persistência (autosave + botão Salvar) ──
  // Produto (custo) + condição de venda preenchidos já bastam para gravar o rascunho.
  // O "Salvar" grava o resultado final e exige o motivo quando o preço não é viável.
  const prontoParaSalvar = temDados && condicaoOk;
  const podeSalvarManual = prontoParaSalvar && precoSolicitadoNum > 0 && motivoOk;

  async function salvarSimulacao(manual = false) {
    if (!prontoParaSalvar) return;
    setStatusSalvamento('salvando');
    try {
      const payload = {
        id: simulacaoIdRef.current || undefined,
        categoria,
        produto_ordem: manual ? null : (produtoSel?.Ordem ?? null),
        produto_codigo: manual ? null : (produtoSel?.Codigo ?? null),
        produto_nome: manual ? (referenciaTexto.trim() || null) : (produtoSel?.Nome ?? null),
        custo_produto: custoNum,
        custo_tabela_nome: manual ? CUSTO_DIGITADO : (custoTabelaNome || null),
        forma_pagamento: formaPagamento,
        num_parcelas: n,
        preco_sugerido: avaliacao ? Number(avaliacao.precoSugerido.toFixed(2)) : null,
        preco_solicitado: precoSolicitadoNum || null,
        desconto_solicitado: precoSolicitadoNum > 0 && avaliacao ? Number((avaliacao.descontoPct * 100).toFixed(2)) : null,
        resultado,
        motivo_tipo: exigeMotivo ? (motivoTipo || null) : null,
        motivo_texto: exigeMotivo && motivoTipo === 'outros' ? (motivoTexto.trim() || null) : null,
        salvo_manualmente: manual,
      };
      const resp = await apiService.simulacaoViabilidade.salvar(payload);
      const novoId = resp.data?.data?.id;
      if (novoId) simulacaoIdRef.current = novoId;
      setStatusSalvamento('salvo');
      if (manual) toast.success(exigeMotivo ? 'Preço liberado e simulação salva!' : 'Simulação salva!');
    } catch {
      setStatusSalvamento('erro');
      if (manual) toast.error('Erro ao salvar simulação.');
    }
  }

  // Autosave com debounce assim que produto + condição de venda estiverem preenchidos.
  useEffect(() => {
    if (!prontoParaSalvar) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { salvarSimulacao(false); }, 1200);
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prontoParaSalvar, categoria, custoNum, custoTabelaNome, produtoSel, origemCusto, referenciaTexto, formaPagamento, parcelas,
    precoSolicitadoNum, motivoTipo, motivoTexto,
  ]);

  async function handleSalvarClick() {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    await salvarSimulacao(true);
  }

  // Linhas da tabela "Preço sugerido por prazo" — inclui o prazo atual se não estiver na lista
  const prazosTabela = [...new Set([...PRAZOS_TABELA, ...(n > 0 ? [n] : [])])].sort((a, b) => a - b);

  // ─── Render ───────────────────────────────────────────────────────────────
  if (!isGerenteOuAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">Acesso Negado</h2>
          <p className="text-gray-600 mt-2">Apenas gerentes ou administradores podem acessar esta página</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-gray-900 mb-1 flex items-center gap-2">
              <Calculator size={20} className="text-gray-400" />
              Simulador de Viabilidade de Preço
            </h1>
            <p className="text-sm text-gray-500">
              Preço sugerido pela política da loja (margem + juros do parcelamento) e se um preço menor pode ser liberado.
            </p>
          </div>
          <button
            onClick={() => navigate('/historico-simulacoes-viabilidade')}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <FileText size={15} />Consultar simulações
          </button>
        </div>

        <div className="mt-4 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <Info size={14} className="flex-shrink-0 mt-0.5 text-blue-600" />
          <p className="text-xs text-blue-800">
            À vista: margem padrão de {fmtPct(PARAMS.markupEletro, 0)} (eletro) e {fmtPct(PARAMS.markupMoveis, 0)} (móveis e demais)
            sobre o custo médio. Parcelado: preço à vista + juros do período ({fmtPct(PARAMS.taxaCartaoAno, 0)} a.a. no cartão,{' '}
            {fmtPct(PARAMS.taxaPropriaAno, 0)} a.a. + {fmtPct(PARAMS.inadimplenciaPropria, 0)} de inadimplência no crediário).
            Este simulador não avalia se o cliente tem crédito — isso é feito na página <strong>Análise de Crédito</strong>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Coluna esquerda — Entradas ── */}
        <div className="space-y-5">

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Produto</p>

            <div className="flex gap-2 mb-4">
              {[
                { value: 'erp', label: 'Buscar produto no ERP' },
                { value: 'manual', label: 'Digitar custo de referência' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => trocarOrigemCusto(opt.value)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors
                    ${origemCusto === opt.value ? 'bg-blue-50 border-blue-400 text-blue-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {manual ? (
            <div className="mb-4">
              <label className="text-xs text-gray-500 block mb-1">Descrição / referência do produto (opcional)</label>
              <input
                type="text"
                value={referenciaTexto}
                onChange={e => setReferenciaTexto(e.target.value)}
                maxLength={200}
                placeholder="Ex: sofá 3 lugares fornecedor X"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              />
            </div>
            ) : (
            <div className="mb-4">
              <label className="text-xs text-gray-500 block mb-1">Buscar produto no ERP (nome, código ou código de barras)</label>
              {produtoSel ? (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-blue-900 truncate">{produtoSel.Nome}</p>
                    <p className="text-xs text-blue-600">
                      {carregandoCusto
                        ? 'Carregando custo...'
                        : custoTabelaNome
                          ? `Custo carregado do ERP (tabela: ${custoTabelaNome})`
                          : 'Sem tabela de Custo Médio — não é possível calcular'}
                    </p>
                  </div>
                  <button onClick={limparProduto} className="text-blue-400 hover:text-blue-700 flex-shrink-0 ml-2">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <form onSubmit={buscarProdutosSimulador} className="flex gap-2">
                  <input
                    type="text"
                    value={produtoTermo}
                    onChange={e => setProdutoTermo(e.target.value)}
                    placeholder="Ex: geladeira, 12345..."
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  />
                  <button
                    type="submit"
                    disabled={buscandoProduto}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Search size={16} />
                  </button>
                </form>
              )}
              {erroProduto && (
                <p className="text-xs text-red-600 mt-1">{erroProduto}</p>
              )}
              {produtoResultados.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {produtoResultados.map(p => (
                    <button
                      key={p.Ordem}
                      onClick={() => selecionarProduto(p)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span className="truncate">{p.Nome}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">#{p.Codigo}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            <div className="flex gap-2 mb-4">
              {[
                { value: 'moveis', label: 'Móveis e demais' },
                { value: 'eletro', label: 'Eletrodomésticos' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setCategoria(opt.value)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors
                    ${categoria === opt.value ? 'bg-blue-50 border-blue-400 text-blue-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="mb-4">
              {manual ? (
              <>
              <label className="text-xs text-gray-500 block mb-1">Custo de referência (R$) — digitado</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={custo}
                onChange={e => setCusto(e.target.value)}
                placeholder="Digite o custo"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              />
              </>
              ) : (
              <>
              <label className="text-xs text-gray-500 block mb-1">Custo do produto (R$) — carregado automaticamente do ERP (tabela Custo Médio)</label>
              <input
                type="number"
                value={custo}
                readOnly
                disabled
                placeholder={produtoSel ? '0,00' : 'Busque um produto para carregar o custo'}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
              />
              </>
              )}
            </div>

            <div className="mb-1 text-xs text-gray-500">
              Preço à vista padrão (margem {fmtPct(markup, 0)}):{' '}
              <span className="font-medium text-gray-700">{temDados ? fmtBRL(precoAvista) : '—'}</span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Condição de venda</p>

            <div className="mb-4">
              <label className="text-xs text-gray-500 block mb-1">Forma de pagamento</label>
              <div className="flex gap-2">
                {Object.entries(FORMAS_PAGAMENTO).map(([value, f]) => (
                  <button
                    key={value}
                    onClick={() => setFormaPagamento(value)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors
                      ${formaPagamento === value ? 'bg-blue-50 border-blue-400 text-blue-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {!avista && (
              <div className="mb-4">
                <label className="text-xs text-gray-500 block mb-1">Número de parcelas (1 a {PARAMS.parcelasMax})</label>
                <input
                  type="number"
                  min="1"
                  max={PARAMS.parcelasMax}
                  value={parcelas}
                  onChange={e => setParcelas(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                />
                {!condicaoOk && (
                  <p className="text-xs text-red-600 mt-1">Informe de 1 a {PARAMS.parcelasMax} parcelas.</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Preço sugerido (R$)</label>
                <input
                  type="text"
                  value={avaliacao ? avaliacao.precoSugerido.toFixed(2) : ''}
                  readOnly
                  disabled
                  placeholder="—"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Preço solicitado (R$)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={precoSolicitado}
                  onChange={e => setPrecoSolicitado(e.target.value)}
                  disabled={!temDados}
                  placeholder={temDados ? 'Digite o preço' : '—'}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 disabled:bg-gray-50"
                />
              </div>
            </div>

            {avaliacao && (
              <p className="mt-2 text-xs text-gray-500">
                {avista
                  ? `Custo + margem de ${fmtPct(markup, 0)}.`
                  : ehAvista(formaPagamento, n)
                  ? `Crédito em 1x segue o preço à vista (custo + margem de ${fmtPct(markup, 0)}).`
                  : `À vista ${fmtBRL(precoAvista)} + juros de ${n} ${n === 1 ? 'mês' : 'meses'} a ${fmtPct(forma.taxaAno, 0)} a.a.`
                    + (forma.inadimplencia > 0 ? ` + ${fmtPct(forma.inadimplencia, 0)} de inadimplência.` : '.')
                    + ` Parcela: ${fmtBRL((precoSolicitadoNum || avaliacao.precoSugerido) / n)}.`}
              </p>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400">
              {statusSalvamento === 'salvando' && 'Salvando...'}
              {statusSalvamento === 'salvo' && 'Simulação salva.'}
              {statusSalvamento === 'erro' && 'Erro ao salvar — tente novamente.'}
              {statusSalvamento === '' && 'Os dados são salvos automaticamente. Clique em Salvar ao chegar no resultado final.'}
            </p>
            <button
              onClick={handleSalvarClick}
              disabled={!podeSalvarManual || carregandoSimulacao}
              title={prontoParaSalvar && precoSolicitadoNum > 0 && !motivoOk ? 'Informe o motivo da liberação' : undefined}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} />
              {exigeMotivo ? 'Liberar e Salvar' : 'Salvar'}
            </button>
          </div>
        </div>

        {/* ── Coluna direita — Resultado ── */}
        <div className="space-y-5">

          {!temDados ? (
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-gray-400">
              Busque um produto ou digite o custo de referência para calcular a viabilidade.
            </div>
          ) : !avaliacao ? null : (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                Resultado — {forma.label}{avista ? '' : ` em ${n}x`}
              </p>

              {!resultado ? (
                <p className="text-sm text-gray-400">Digite o preço solicitado para verificar a viabilidade.</p>
              ) : (
                <>
                  <BadgeResultado resultado={resultado} />

                  {resultado === 'gerente' && (
                    <div className="mt-3 bg-red-50 border border-red-300 rounded-lg px-3 py-2 text-sm text-red-700 flex items-start gap-2">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                      <span>
                        Preço abaixo do padrão — <strong>necessita liberação do(a) gerente</strong>.
                      </span>
                    </div>
                  )}
                  {resultado === 'inviavel' && (
                    <div className="mt-3 bg-red-100 border border-red-500 rounded-lg px-3 py-2 text-sm text-red-800 flex items-start gap-2">
                      <XCircle size={16} className="flex-shrink-0 mt-0.5" />
                      <span>
                        Preço fora da política de desconto. Só é possível com justificativa
                        (ex.: produto em promoção, com avaria ou estoque antigo).
                      </span>
                    </div>
                  )}

                  <dl className="mt-4 space-y-1.5 text-sm">
                    <div className="flex justify-between"><dt className="text-gray-500">Preço sugerido</dt><dd className="font-medium text-gray-800">{fmtBRL(avaliacao.precoSugerido)}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">Preço solicitado</dt><dd className="font-medium text-gray-800">{fmtBRL(precoSolicitadoNum)}</dd></div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Desconto sobre o sugerido</dt>
                      <dd className="font-medium text-gray-800">{avaliacao.descontoPct > 0 ? fmtPct(avaliacao.descontoPct) : '—'}</dd>
                    </div>
                  </dl>

                  {exigeMotivo && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Motivo da liberação (gerente)</p>
                      <div className="space-y-2">
                        {Object.entries(MOTIVOS_LIBERACAO).map(([value, label]) => (
                          <label key={value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="radio"
                              name="motivoLiberacao"
                              value={value}
                              checked={motivoTipo === value}
                              onChange={() => setMotivoTipo(value)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                      {motivoTipo === 'outros' && (
                        <textarea
                          value={motivoTexto}
                          onChange={e => setMotivoTexto(e.target.value)}
                          maxLength={500}
                          rows={3}
                          placeholder="Descreva o motivo (obrigatório)"
                          className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                        />
                      )}
                      {!motivoOk && (
                        <p className="text-xs text-red-600 mt-1">
                          {motivoTipo === 'outros' ? 'Descreva o motivo para poder salvar.' : 'Selecione o motivo para poder salvar.'}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Preço sugerido por prazo</p>
              <p className="text-xs text-gray-400">{categoria === 'moveis' ? 'Móveis e demais' : 'Eletrodomésticos'}</p>
            </div>
            <p className="text-xs text-gray-400 mb-3">Entre parênteses, o acréscimo total sobre o custo médio.</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left font-normal pb-1.5">Prazo</th>
                  <th className="text-right font-normal pb-1.5">Cartão</th>
                  <th className="text-right font-normal pb-1.5">Crediário</th>
                </tr>
              </thead>
              <tbody>
                <tr className={avista ? 'bg-blue-50' : ''}>
                  <td className={`py-1.5 ${avista ? 'text-blue-800 font-medium' : 'text-gray-600'}`}>À vista</td>
                  <td colSpan={2} className={`py-1.5 text-right whitespace-nowrap ${avista ? 'text-blue-800 font-medium' : 'text-gray-700'}`}>
                    {custoNum > 0 ? `${fmtBRL(precoAvista)} (${fmtPct(markup, 0)})` : '—'}
                  </td>
                </tr>
                {prazosTabela.map(p => {
                  const linhaAtiva = !avista && p === n;
                  const celula = (f) => {
                    const ativa = linhaAtiva && formaPagamento === f;
                    const preco = custoNum > 0 ? calcPrecoSugerido({ custo: custoNum, categoria, forma: f, n: p }) : null;
                    return (
                      <td className={`py-1.5 text-right whitespace-nowrap ${ativa ? 'text-blue-800 font-semibold' : 'text-gray-700'}`}>
                        {preco !== null ? <>{fmtBRL(preco)} <span className="text-xs text-gray-400">({fmtPct(preco / custoNum - 1, 0)})</span></> : '—'}
                      </td>
                    );
                  };
                  return (
                    <tr key={p} className={linhaAtiva ? 'bg-blue-50' : ''}>
                      <td className={`py-1.5 ${linhaAtiva ? 'text-blue-800 font-medium' : 'text-gray-600'}`}>{p}x</td>
                      {celula('cartao')}
                      {celula('crediario')}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
